import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import Select from '../../../components/ui/Select';
import apiService from '../../../utils/apiService';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
    organizationDomain: '',
    role: 'owner',
    agreeToTerms: false,
    agreeToPrivacy: false,
    organizationId: '',
    registrationType: 'create', // 'create' or 'join'
  });
  const [publicOrganizations, setPublicOrganizations] = useState([]);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEmailDomain = async (email, organizationDomain) => {
    if (!email || !organizationDomain) return { isValid: true, message: '' };

    const emailDomain = email.split('@')[1]?.toLowerCase();
    const orgDomain = organizationDomain.toLowerCase();

    // For new organization registration, email domain should match organization domain
    const isValid = emailDomain === orgDomain;
    const message = isValid
      ? ''
      : `Email domain (${emailDomain}) must match organization domain (${orgDomain})`;

    return { isValid, message };
  };

  React.useEffect(() => {
    const fetchOrgs = async () => {
      if (formData.registrationType === 'join') {
        try {
          const result = await apiService.organizations.getPublic();
          setPublicOrganizations(result || []);
        } catch (error) {
          console.error('Failed to fetch public organizations:', error);
        }
      }
    };
    fetchOrgs();
  }, [formData.registrationType]);

  const validatePassword = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const validateDomain = (domain) => {
    const domainRegex =
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear specific field error
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }

    // Real-time password strength validation
    if (field === 'password') {
      setPasswordStrength(validatePassword(value));
    }
  };

  const validateStep1 = async () => {
    const newErrors = {};

    const { firstName, lastName, email, password, confirmPassword } = formData;

    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    else if (firstName.trim().length < 2) newErrors.firstName = 'First name must be at least 2 characters';

    if (!lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (lastName.trim().length < 2) newErrors.lastName = 'Last name must be at least 2 characters';

    if (!email.trim()) newErrors.email = 'Email address is required';
    else if (!validateEmail(email)) newErrors.email = 'Please enter a valid email address';

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength < 3) {
      newErrors.password = 'Password is too weak (mix uppercase, numbers, symbols)';
    }

    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = async () => {
    const newErrors = {};

    const { registrationType, organizationName, organizationDomain, organizationId, agreeToTerms, agreeToPrivacy, email } = formData;

    if (registrationType === 'create') {
      if (!organizationName.trim()) newErrors.organizationName = 'Organization name is required';
      
      if (!organizationDomain.trim()) {
        newErrors.organizationDomain = 'Organization domain is required';
      } else if (!validateDomain(organizationDomain)) {
        newErrors.organizationDomain = 'Invalid domain format (e.g., example.com)';
      } else if (email) {
        const { isValid, message } = await validateEmailDomain(email, organizationDomain);
        if (!isValid) newErrors.organizationDomain = message;
      }
    } else if (registrationType === 'join' && !organizationId) {
      newErrors.organizationId = 'Please select an organization to join';
    }

    if (!agreeToTerms) newErrors.agreeToTerms = 'Terms agreement is required';
    if (!agreeToPrivacy) newErrors.agreeToPrivacy = 'Privacy agreement is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = async () => {
    if (await validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateStep2();
    if (!isValid) {
      setErrors(prev => ({ ...prev, submit: 'Please correct the errors in the form before continuing.' }));
      return;
    }

    setIsLoading(true);

    try {
      // Use the separate first and last name fields
      const firstName = formData.firstName.trim();
      const lastName = formData.lastName.trim();

      // Call the enhanced registration API
      const result = await register(formData.email, formData.password, {
        firstName,
        lastName,
        organizationName: formData.registrationType === 'create' ? formData.organizationName : '',
        organizationDomain: formData.registrationType === 'create' ? formData.organizationDomain : '',
        organizationId: formData.registrationType === 'join' ? formData.organizationId : '',
      });

      if (!result.success) {
        setErrors({ submit: result.error });
        return;
      }

      // Registration successful - navigate to dashboard
      console.log('Registration successful:', result.data);

      // Navigate to success or pending page
      if (formData.registrationType === 'join') {
        navigate('/registration-pending', {
          state: { email: formData.email }
        });
      } else {
        navigate('/role-based-dashboard', {
          state: {
            message: `Welcome to ${formData.organizationName}! You are now the organization owner. A welcome email has been sent to ${formData.email}.`,
            type: 'success',
            isNewUser: true,
          },
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        submit: error.message || 'Registration failed. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'Weak';
      case 2:
      case 3:
        return 'Medium';
      case 4:
      case 5:
        return 'Strong';
      default:
        return '';
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'bg-destructive';
      case 2:
      case 3:
        return 'bg-warning';
      case 4:
      case 5:
        return 'bg-success';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className='w-full max-w-md mx-auto'>
      <div className='bg-card rounded-lg shadow-enterprise border border-border p-8'>
        {/* Header */}
        <div className='text-center mb-8'>
          <h1 className='text-2xl font-semibold text-text-primary mb-2'>
            Create Your Account
          </h1>
          <p className='text-text-secondary'>
            {currentStep === 1
              ? 'Enter your personal information to get started'
              : 'Set up your organization workspace'}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className='flex items-center justify-center mb-8'>
          <div className='flex items-center space-x-4'>
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                currentStep >= 1
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border text-text-secondary'
              }`}
            >
              {currentStep > 1 ? <Icon name='Check' size={16} /> : '1'}
            </div>
            <div
              className={`w-12 h-0.5 ${
                currentStep > 1 ? 'bg-primary' : 'bg-border'
              }`}
            />
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                currentStep >= 2
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border text-text-secondary'
              }`}
            >
              2
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='space-y-6'>
          {currentStep === 1 && (
            <>
              {/* Step 1: Personal Information */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Input
                  label='First Name'
                  type='text'
                  placeholder='Enter your first name'
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={errors.firstName}
                  required
                />
                <Input
                  label='Last Name'
                  type='text'
                  placeholder='Enter your last name'
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={errors.lastName}
                  required
                />
              </div>

              <Input
                label='Email Address'
                type='email'
                placeholder='Enter your email address'
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={errors.email}
                description="We'll use this email for account verification and notifications"
                required
              />

              <div className='space-y-2'>
                <Input
                  label='Password'
                  type='password'
                  placeholder='Create a strong password'
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange('password', e.target.value)
                  }
                  error={errors.password}
                  required
                />

                {formData.password && (
                  <div className='space-y-2'>
                    <div className='flex items-center justify-between text-sm'>
                      <span className='text-text-secondary'>
                        Password strength:
                      </span>
                      <span
                        className={`font-medium ${
                          passwordStrength <= 1
                            ? 'text-destructive'
                            : passwordStrength <= 3
                            ? 'text-warning'
                            : 'text-success'
                        }`}
                      >
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                    <div className='w-full bg-muted rounded-full h-2'>
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      />
                    </div>

                    {/* Password Requirements */}
                    <div className='text-xs text-text-secondary space-y-1'>
                      <div className='font-medium mb-1'>Password must include:</div>
                      <div className={`flex items-center space-x-2 ${formData.password.length >= 8 ? 'text-success' : ''}`}>
                        <Icon name={formData.password.length >= 8 ? 'Check' : 'X'} size={12} />
                        <span>At least 8 characters</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${/[A-Z]/.test(formData.password) ? 'text-success' : ''}`}>
                        <Icon name={/[A-Z]/.test(formData.password) ? 'Check' : 'X'} size={12} />
                        <span>One uppercase letter</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${/[a-z]/.test(formData.password) ? 'text-success' : ''}`}>
                        <Icon name={/[a-z]/.test(formData.password) ? 'Check' : 'X'} size={12} />
                        <span>One lowercase letter</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${/[0-9]/.test(formData.password) ? 'text-success' : ''}`}>
                        <Icon name={/[0-9]/.test(formData.password) ? 'Check' : 'X'} size={12} />
                        <span>One number</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${/[^A-Za-z0-9]/.test(formData.password) ? 'text-success' : ''}`}>
                        <Icon name={/[^A-Za-z0-9]/.test(formData.password) ? 'Check' : 'X'} size={12} />
                        <span>One special character</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Input
                label='Confirm Password'
                type='password'
                placeholder='Confirm your password'
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange('confirmPassword', e.target.value)
                }
                error={errors.confirmPassword}
                required
              />

              <Button
                type='button'
                onClick={handleNextStep}
                className='w-full'
                iconName='ArrowRight'
                iconPosition='right'
              >
                Continue
              </Button>
            </>
          )}

          {currentStep === 2 && (
            <>
              {/* Step 2: Organization Setup */}
              <div className='flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl mb-6'>
                <button
                  type='button'
                  onClick={() => handleInputChange('registrationType', 'create')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition-all ${
                    formData.registrationType === 'create'
                      ? 'bg-white dark:bg-slate-800 shadow-sm text-primary'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Create New Workspace
                </button>
                <button
                  type='button'
                  onClick={() => handleInputChange('registrationType', 'join')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition-all ${
                    formData.registrationType === 'join'
                      ? 'bg-white dark:bg-slate-800 shadow-sm text-primary'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Join Existing
                </button>
              </div>

              {formData.registrationType === 'create' ? (
                <>
                  <Input
                    label='Organization Name'
                    type='text'
                    placeholder='Enter your organization name'
                    value={formData.organizationName}
                    onChange={(e) =>
                      handleInputChange('organizationName', e.target.value)
                    }
                    error={errors.organizationName}
                    description='This will be the name of your workspace'
                    required
                  />

                  <Input
                    label='Organization Domain'
                    type='text'
                    placeholder='example.com'
                    value={formData.organizationDomain}
                    onChange={(e) =>
                      handleInputChange('organizationDomain', e.target.value)
                    }
                    error={errors.organizationDomain}
                    description='Used for email invitations and domain restrictions'
                    required
                  />
                </>
              ) : (
                <div className='space-y-4'>
                  <Select
                    label='Select Organization'
                    placeholder='Search or select an organization'
                    options={publicOrganizations
                      .filter(org => {
                        const emailDomain = formData.email.split('@')[1]?.toLowerCase();
                        return !org.domain || org.domain.toLowerCase() === emailDomain;
                      })
                      .map(org => ({ value: org.id, label: org.name }))}
                    value={formData.organizationId}
                    onChange={(val) => handleInputChange('organizationId', val)}
                    error={errors.organizationId}
                    description={`Showing organizations matching your domain: ${formData.email.split('@')[1] || ''}`}
                    required
                  />
                  {publicOrganizations.length === 0 && (
                    <p className='text-xs text-amber-600 font-medium'>
                      No organizations found. You might need to create one.
                    </p>
                  )}
                </div>
              )}

              {/* Terms and Privacy */}
              <div className='space-y-4'>
                <Checkbox
                  label='I agree to the Terms of Service'
                  checked={formData.agreeToTerms}
                  onChange={(e) =>
                    handleInputChange('agreeToTerms', e.target.checked)
                  }
                  error={errors.agreeToTerms}
                  required
                />

                <Checkbox
                  label='I agree to the Privacy Policy'
                  checked={formData.agreeToPrivacy}
                  onChange={(e) =>
                    handleInputChange('agreeToPrivacy', e.target.checked)
                  }
                  error={errors.agreeToPrivacy}
                  required
                />
              </div>


              {/* Action Buttons */}
              <div className='flex space-x-3'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handlePreviousStep}
                  className='flex-1'
                  iconName='ArrowLeft'
                  iconPosition='left'
                  disabled={isLoading}
                >
                  Back
                </Button>

                <Button
                  type='submit'
                  loading={isLoading}
                  className='flex-1'
                  iconName={formData.registrationType === 'create' ? 'UserPlus' : 'Send'}
                  iconPosition='left'
                >
                  {formData.registrationType === 'create' ? 'Create Account' : 'Join Workspace'}
                </Button>
              </div>

              {errors.submit && (
                <div className='p-3 bg-destructive/10 border border-destructive/20 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300'>
                  <div className='flex items-start gap-2'>
                    <Icon name='AlertCircle' size={16} className='text-destructive mt-0.5 shrink-0' />
                    <p className='text-sm text-destructive font-medium'>{errors.submit}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </form>

        {/* Footer */}
        <div className='mt-8 pt-6 border-t border-border text-center'>
          <p className='text-sm text-text-secondary'>
            Already have an account?{' '}
            <Link
              to='/login'
              className='font-medium text-primary hover:text-primary/80 transition-colors'
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>

      {/* Additional Information */}
      <div className='mt-6 text-center'>
        <p className='text-xs text-text-secondary'>
          By creating an account, you'll be able to invite team members and
          manage projects.
          <br />
          Need to join an existing organization? Contact your administrator for
          an invitation.
        </p>
      </div>
    </div>
  );
};

export default RegistrationForm;
